import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup 
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  prjSalesHeaderTyps,
  prjRealSalesProfitHeaderTyps,
  prjDeptProfitHeaders,
  prjTargetRateProfitHeaderTyps,
  prjExTargetRateHeaderTyps,
  prjStatProfitHeaderTyps,
} from '../../utils/forecastStaticData';
import ProfitStaticsHeadquerterRows from './ProfitStaticsHeadquerterRows';

const useStyles = makeStyles({
  table: {
    '& .MuiTableCell-root': {
      border: '1px solid rgba(224, 224, 224, 1)',
      whiteSpace: 'nowrap', 
      padding: '6px 4px',
    },
    '& .MuiTableCell-head': {
      fontWeight: 'bold',
      whiteSpace: 'pre-line', // 헤더 \n 처리
      lineHeight: 1.1,
    },
  },
});

const ProfitStaticsHeadquarterTable = ({ data, tableRef, selectPrjTyp, strMonth, endMonth }) => {
  const classes = useStyles();
  const [showKorean, setShowKorean] = useState(false);

  const getBgColor = (id) => {
    if (id?.includes('STAT')) return '#b0deff';
    if (id === 'OA') return '#e3f2fd';
    if (id === 'HM') return '#fff3e0';
    if (id === 'BUDGETAMT') return '#e8f5e9';
    if (id === 'SELLINGEX') return '#fff3e0';
    return '#f5f5f5';
  };

  const getHeaderTyps = (id) => {
    if (id === 'EXSTATRATE') return prjExTargetRateHeaderTyps;
    if (id === 'STATRATE') return prjTargetRateProfitHeaderTyps;
    if (id?.includes('STAT')) return prjStatProfitHeaderTyps;
    if (id === 'OA') return prjRealSalesProfitHeaderTyps;
    return prjSalesHeaderTyps;
  };

  const allHeaders = prjDeptProfitHeaders(strMonth, endMonth, '매출');
  const filteredHeaders = allHeaders.filter((header) => 
    header.id.includes('STAT') ? selectPrjTyp === 'A' : true
  );

  const totalColCount = 1 + filteredHeaders.reduce((acc, header) => {
    const isSingleCol = header.id === 'SELLINGEX' || header.id === 'BUDGETAMT';
    return acc + (isSingleCol ? 1 : getHeaderTyps(header.id).length);
  }, 0);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, px: 1 }}>
        <ToggleButtonGroup
          value={showKorean}
          exclusive
          onChange={(e, nextValue) => { if (nextValue !== null) setShowKorean(nextValue); }}
          size="small"
          sx={{
            height: '32px',
            '& .MuiToggleButton-root': {
              fontSize: '0.75rem', fontWeight: 700, color: '#455a64', px: 1.5, border: '1px solid #cfd8dc', 
              '&.Mui-selected': { backgroundColor: '#1967d2', color: 'white' },
            },
          }}
        >
          <ToggleButton value={false}>숫자 적용</ToggleButton>
          <ToggleButton value={true}>한글 적용</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ color: '#455a64', fontWeight: 700, fontSize: '0.75rem', marginLeft: 'auto' }}>
          (단위: {showKorean ? '한글 혼용' : '원'})
        </Typography>
      </Box>
   
      <Paper sx={{ my: 1, width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ width: '100%', maxHeight: '70vh' }}>
          <Table 
            size="small" 
            className={classes.table} 
            ref={tableRef} 
            stickyHeader
            sx={{ width: 'max-content', minWidth: '100%', tableLayout: 'fixed' }}
          >
            <colgroup>
              <col style={{ width: '120px', minWidth: '120px' }} />
              {filteredHeaders.flatMap((header) => {
                const isSingleCol = header.id === 'SELLINGEX' || header.id === 'BUDGETAMT';
                if (isSingleCol) return <col key={`col-hq-${header.id}`} style={{ width: '100px', minWidth: '100px' }} />;
                
                return getHeaderTyps(header.id).map((typ, tIdx) => {
                  const isPercent = typ.name?.includes('%') || typ.id?.toLowerCase().includes('rate') || typ.id === 'salesProfitMargin';
                  const colWidth = isPercent ? '45px' : '100px'; 
                  return <col key={`col-hq-${header.id}-${tIdx}`} style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }} />;
                });
              })}
            </colgroup>

            <TableHead>
              <TableRow>
                <TableCell align="center" rowSpan={2} sx={{ bgcolor: '#f5f5f5', zIndex: 11, fontSize: '0.85rem', fontWeight: 'bold' }}>본부</TableCell>
                {filteredHeaders.map((header) => {
                  const isSingleCol = header.id === 'SELLINGEX' || header.id === 'BUDGETAMT';
                  return (
                    <TableCell
                      key={header.val}
                      align="center"
                      rowSpan={isSingleCol ? 2 : 1}
                      colSpan={isSingleCol ? 1 : getHeaderTyps(header.id).length}
                      sx={{ bgcolor: getBgColor(header.id), fontWeight: 800, fontSize: '0.9rem', whiteSpace: 'pre-line' }}
                    >
                      {header.name}
                    </TableCell>
                  );
                })}
              </TableRow>
              <TableRow>
                {filteredHeaders.map((header) => {
                  if (header.id === 'SELLINGEX' || header.id === 'BUDGETAMT') return null;
                  return getHeaderTyps(header.id).map((typ, index) => {
                    const isPercent = typ.name?.includes('%') || typ.id?.toLowerCase().includes('rate') || typ.id === 'salesProfitMargin';
                    return (
                      <TableCell 
                        key={`${header.id}-${index}`}
                        align="center" 
                        sx={{ 
                          bgcolor: getBgColor(header.id), fontSize: '0.8rem', fontWeight: 700,
                          padding: isPercent ? '4px 0px' : '6px 2px',
                        }}
                      >
                        {typ.name}
                      </TableCell>
                    );
                  });  
              })}
              </TableRow>
            </TableHead>

            <TableBody>
              {!data || data.length === 0 ? (
                <TableRow><TableCell colSpan={totalColCount} align="center" sx={{ py: 5, color: '#1a73e8', fontWeight: 600 }}>데이터가 없습니다.</TableCell></TableRow>
              ) : (
                <ProfitStaticsHeadquerterRows useKorean={showKorean} headerRows={filteredHeaders} data={data} selectPrjTyp={selectPrjTyp} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
export default ProfitStaticsHeadquarterTable;
